import { useEffect } from "react";
import * as THREE from "three";
import { logger } from "../../utils/logger";

export function useScenePhysicsLifecycle({ isPhysicsRunning, jointObjectsRef, sceneObjectsRef }) {
  useEffect(() => {
    window.sceneObjectsRef = sceneObjectsRef;
    return () => {
      delete window.sceneObjectsRef;
    };
  }, [sceneObjectsRef]);

  // Normalize any kinematics pivot groups when physics starts to avoid double transforms.
  useEffect(() => {
    if (!isPhysicsRunning) return;
    try {
      const entries = Object.values(jointObjectsRef.current || {});
      entries.forEach((entry) => {
        const bodyGroup = entry && entry.bodyGroup;
        if (!bodyGroup) return;
        const pivot = bodyGroup.userData && bodyGroup.userData._pivotGroup;
        if (!pivot || !pivot.parent) return;

        const worldPos = new THREE.Vector3();
        const worldQuat = new THREE.Quaternion();
        bodyGroup.getWorldPosition(worldPos);
        bodyGroup.getWorldQuaternion(worldQuat);

        const newParent = pivot.parent;
        newParent.add(bodyGroup);
        try {
          pivot.parent.remove(pivot);
        } catch (error) {
          logger.warn("[Scene] Unexpected error during scene update", error);
        }
        delete bodyGroup.userData._pivotGroup;
        delete bodyGroup.userData._pivotLocal;

        const localPos = worldPos.clone();
        if (typeof newParent.worldToLocal === "function") newParent.worldToLocal(localPos);
        const parentWorldQuat = new THREE.Quaternion();
        if (typeof newParent.getWorldQuaternion === "function") {
          newParent.getWorldQuaternion(parentWorldQuat);
        }
        const localQuat = parentWorldQuat.clone().invert().multiply(worldQuat);
        bodyGroup.position.copy(localPos);
        bodyGroup.quaternion.copy(localQuat);
      });
    } catch (e) {
      logger.warn("[Physics] Failed to normalize pivot groups:", e);
    }
  }, [isPhysicsRunning, jointObjectsRef]);

  useEffect(() => {
    const handlePhysicsStopReset = () => {
      // Intentionally no-op: physics stop already dispatches `structure-changed`,
      // which triggers a clean rebuild. Keep this event for other listeners.
    };

    window.addEventListener("physics-stopped-reset", handlePhysicsStopReset);
    return () => window.removeEventListener("physics-stopped-reset", handlePhysicsStopReset);
  }, []);
}
